import { create } from 'zustand'
import type { SyncQueueItem } from '@/types'
import { flushSyncQueue, pendingSyncItems } from '@/services/api'

interface SyncState {
  isOnline: boolean
  /** True while queued mutations are being replayed. */
  syncing: boolean
  lastSyncedAt: string | null
  queue: SyncQueueItem[]
  /** Set by the Workbox registration when a new build is waiting. */
  updateReady: boolean
  applyUpdate: (() => void) | null

  setOnline: (isOnline: boolean) => void
  refreshQueue: () => Promise<void>
  flush: () => Promise<void>
  setUpdateReady: (ready: boolean, apply?: () => void) => void
}

export const useSyncStore = create<SyncState>((set, get) => ({
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  syncing: false,
  lastSyncedAt: null,
  queue: [],
  updateReady: false,
  applyUpdate: null,

  setOnline(isOnline) {
    const wasOffline = !get().isOnline
    set({ isOnline })
    // Coming back online is the natural moment to drain the queue.
    if (isOnline && wasOffline) void get().flush()
  },

  async refreshQueue() {
    set({ queue: await pendingSyncItems() })
  },

  async flush() {
    if (get().syncing || !get().isOnline) return
    set({ syncing: true })
    try {
      await flushSyncQueue()
      set({ lastSyncedAt: new Date().toISOString() })
    } finally {
      set({ syncing: false })
      await get().refreshQueue()
    }
  },

  setUpdateReady(ready, apply) {
    set({ updateReady: ready, applyUpdate: apply ?? null })
  },
}))

/** Wires browser connectivity events into the store. Call once, at boot. */
export function initConnectivityListeners(): () => void {
  const online = () => useSyncStore.getState().setOnline(true)
  const offline = () => useSyncStore.getState().setOnline(false)
  window.addEventListener('online', online)
  window.addEventListener('offline', offline)
  void useSyncStore.getState().refreshQueue()
  return () => {
    window.removeEventListener('online', online)
    window.removeEventListener('offline', offline)
  }
}
